/**
 * A7Y-2821 driver leases — who currently holds each coding account, what work
 * phase they're in, and what the control plane refuses.
 *
 * WHAT THIS PANEL IS FOR. The lease is the single global mutual-exclusion point
 * for a Claude/Codex account; `concurrency:` groups cannot provide it because
 * they are per-workflow and the accounts are shared across six. When the fleet
 * stalls, the two questions are "who is holding it" and "why was the next thing
 * refused", and until this panel existed neither was answerable without reading
 * Firestore by hand.
 *
 * NOTHING HERE STATES A BOUNDARY. Every percentage — the operating ceiling, the
 * provider limit — arrives in the payload, derived from `POLICY`. A missing
 * value renders as an explicit unknown rather than as a guessed number, because
 * a guessed boundary is a second admission regime wearing a fallback (A7Y-2821
 * §2). The lease's `phase` (A7Y-3699) is descriptive only — it buys no
 * capacity of its own.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, HelpCircle } from 'lucide-react';
import type { AutomationCircuitState, AutomationHandoff, AutomationLease } from '@a7y/types';
import { api } from '@/lib/api';
import { CapacityConcepts } from './CapacityConcepts';
import { fmtDuration, fmtLocal, useNow } from './shared';
// STATE_META/UNKNOWN_META live in their own pure module (not this .tsx) so
// SlotsSections.tsx (A7Y-3600) can reuse the same chip palette without a
// second copy — `react-refresh/only-export-components` refuses a non-component
// export from a file that also exports a component, which is why this isn't
// just an `export` here.
import { STATE_META, UNKNOWN_META } from './lease-state-meta';

/** A future instant as a countdown; a past one says so rather than counting up. */
function expiryText(expiresAt: string, nowMs: number): string {
  const delta = Date.parse(expiresAt) - nowMs;
  if (!Number.isFinite(delta)) return 'expiry not recorded';
  return delta <= 0 ? `expired ${fmtDuration(-delta / 1000)} ago` : `expires in ${fmtDuration(delta / 1000)}`;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="shrink-0 text-zinc-500">{label}</span>
      <span className="min-w-0 text-right text-zinc-800 dark:text-zinc-200">{children}</span>
    </div>
  );
}

const Unknown = ({ children }: { children: React.ReactNode }) => (
  <span className="text-zinc-400">{children}</span>
);

/**
 * A7Y-3699 — the lease no longer sizes a reservation envelope; the FIRST
 * holder carries only a descriptive work-phase label. It buys no capacity —
 * see `CapacityConcepts` for the admission verdict itself.
 */
function PhaseLabel({ lease }: { lease: AutomationLease }) {
  if (lease.readError) return <Unknown>not readable</Unknown>;
  if (!lease.phase) return <Unknown>no phase recorded</Unknown>;
  return <span className="font-mono">{lease.phase}</span>;
}

function LeaseCard({ lease, nowMs }: { lease: AutomationLease; nowMs: number }) {
  const meta = lease.state ? STATE_META[lease.state] : UNKNOWN_META;
  return (
    <div className={`rounded-lg border bg-white p-3 dark:bg-zinc-900 ${meta.card}`}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {lease.account}
          </div>
          <div className="text-[11px] uppercase tracking-wide text-zinc-400">
            {lease.purpose ? lease.purpose : 'no purpose recorded'}
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${meta.chip}`}>
          {meta.label}
        </span>
      </div>

      {lease.readError ? (
        <div className="mb-3 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            This lease could not be read, so its state is unknown — not idle: {lease.readError}
          </span>
        </div>
      ) : null}

      {!lease.readError && !lease.documentExists ? (
        <p className="mb-3 text-[11px] leading-relaxed text-zinc-500">
          No lease document — this account has never been leased.
        </p>
      ) : null}

      <div className="space-y-1.5">
        <Row label="Generation">
          {lease.generation != null ? (
            <span className="font-mono tabular-nums">{lease.generation}</span>
          ) : (
            <Unknown>not recorded</Unknown>
          )}
        </Row>
        {/*
          A7Y-3285. The lease is a counted semaphore, so the operator question
          is "how full is this account", not "is anybody on it". The count is
          rendered as its own row and the holder row shows the FIRST holder,
          saying plainly how many others there are. Deliberately minimal — no
          per-holder list, and no dual-format rendering of the pre-semaphore
          document shape.
        */}
        <Row label="Holders">
          <span className="font-mono tabular-nums">{lease.holderCount}</span>
        </Row>
        <Row label={lease.holderCount > 1 ? 'First holder' : 'Holder'}>
          {lease.holder?.runUrl ? (
            <span className="inline-flex items-center gap-1">
              <a
                href={lease.holder.runUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
              >
                <span className="truncate">{lease.holder.job ?? lease.holder.runId}</span>
                <ExternalLink className="h-3 w-3 shrink-0" />
              </a>
              {lease.holderCount > 1 ? (
                <span className="text-zinc-400">+{lease.holderCount - 1} more</span>
              ) : null}
            </span>
          ) : lease.holder?.workflow ? (
            <span className="truncate">
              {lease.holder.workflow}
              {lease.holderCount > 1 ? (
                <span className="ml-1 text-zinc-400">+{lease.holderCount - 1} more</span>
              ) : null}
            </span>
          ) : (
            <Unknown>nobody</Unknown>
          )}
        </Row>
        <Row label="Expiry">
          {lease.expiresAt ? (
            <span title={fmtLocal(lease.expiresAt)}>{expiryText(lease.expiresAt, nowMs)}</span>
          ) : (
            <Unknown>not recorded</Unknown>
          )}
        </Row>
        <Row label="Phase">
          <PhaseLabel lease={lease} />
        </Row>
        {lease.quarantine ? (
          <Row label="Quarantined until">
            {lease.quarantine.until ? (
              <span title={lease.quarantine.reason ?? undefined}>
                {fmtLocal(lease.quarantine.until)}
              </span>
            ) : (
              // The store keeps an unreadable `untilMs` quarantined rather than
              // releasing it, so this is "held indefinitely", not "not set".
              <Unknown>no readable expiry — held</Unknown>
            )}
          </Row>
        ) : null}
        {lease.ticketIds.length > 0 ? (
          <Row label="Tickets">
            <span className="font-mono text-[11px]">{lease.ticketIds.join(', ')}</span>
          </Row>
        ) : null}
      </div>

      <div className="mt-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
        <CapacityConcepts capacity={lease.capacity} />
      </div>
    </div>
  );
}

function HandoffPanel({ handoffs, circuit }: { handoffs: AutomationHandoff[]; circuit: AutomationCircuitState }) {
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
        <div className="mb-2 text-[11px] uppercase tracking-wide text-zinc-400">
          Handoffs in flight
        </div>
        {handoffs.length === 0 ? (
          <p className="text-xs text-zinc-500">
            No lease is parked in <code>handoff_pending</code>.
          </p>
        ) : (
          <ul className="space-y-2">
            {handoffs.map((handoff) => (
              <li key={handoff.account} className="text-xs">
                <div className="font-medium text-zinc-900 dark:text-zinc-100">
                  {handoff.account}
                  <span className="ml-2 font-normal text-zinc-500">
                    {handoff.ageMs != null ? (
                      `waiting ${fmtDuration(handoff.ageMs / 1000)}`
                    ) : (
                      // The watchdog reports this as its own unknown: an age it
                      // cannot compute may be seconds or days.
                      <span className="text-zinc-400">age not computable</span>
                    )}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] text-zinc-500">
                  {handoff.reason ?? 'no incident recorded'}
                  {handoff.generation != null ? ` · gen ${handoff.generation}` : ''}
                  {handoff.ticketIds.length > 0 ? ` · ${handoff.ticketIds.join(', ')}` : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-[10px] leading-relaxed text-zinc-400">
          A handoff holds its account while nothing runs on it. The ten-minute bound belongs to the
          control watchdog, which pages; this panel reports the age rather than restating the bound.
        </p>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-[11px] uppercase tracking-wide text-zinc-400">
            Scheduler circuit
          </span>
          {circuit.state === 'open' ? (
            <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800 dark:bg-red-950 dark:text-red-300">
              Open — dispatch suppressed
            </span>
          ) : circuit.state === 'closed' ? (
            <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              Closed
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
              <HelpCircle className="h-3 w-3" />
              Unknown
            </span>
          )}
        </div>
        {circuit.state === 'open' ? (
          <div className="space-y-1 text-xs text-zinc-700 dark:text-zinc-300">
            <div>
              Partition <span className="font-mono">{circuit.partition ?? 'unrecorded'}</span>
              {circuit.openedAt ? ` · opened ${fmtLocal(circuit.openedAt)}` : ''}
            </div>
            <div className="text-zinc-500">
              {circuit.passes ?? '?'} identical non-progress passes
              {circuit.bound != null ? ` against a bound of ${circuit.bound}` : ''}
            </div>
          </div>
        ) : null}
        {circuit.reason ? (
          <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">{circuit.reason}</p>
        ) : null}
      </div>
    </div>
  );
}

export function LeaseStatusSection() {
  const nowMs = useNow(1_000);
  const leases = useQuery({
    queryKey: ['autobuild-leases'],
    queryFn: () => api.getAutomationLeaseStatus(),
    refetchInterval: 15_000,
    refetchOnWindowFocus: false,
  });

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
          Account leases
        </h2>
        <p className="mt-0.5 text-xs text-zinc-500">
          One lease per coding account, fenced by a monotonic generation. Every process that spends
          an account&apos;s quota holds its lease first.
        </p>
      </div>

      {leases.isError ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Lease status failed to load, so nothing below is known — check the Worker logs for
          /admin/autobuild/leases.
        </div>
      ) : null}

      {leases.isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-60 animate-pulse rounded-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900"
            />
          ))}
        </div>
      ) : leases.data ? (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {leases.data.leases.map((lease) => (
              <LeaseCard key={lease.account} lease={lease} nowMs={nowMs} />
            ))}
          </div>
          <HandoffPanel handoffs={leases.data.handoffs} circuit={leases.data.circuit} />
        </>
      ) : null}
    </section>
  );
}
